import React from 'react';
import dayjs from 'dayjs';
import {useFormContext} from 'react-hook-form';
import {
  Guest,
  PhoneDetails,
  SelectOption,
  SessionStorage,
  FIELD_TYPES,
  GUEST_FORM_NAMES,
} from '@chekin/core';
import {FormTypes, EXTRA_FORM_NAMES, buildGuestFormDataFromDB} from '../utils/guestForm';
import {usePreloadPoliceData} from 'components/GuestFormFields/usePreloadPoliceData';
import {STORAGE_KEYS} from '../utils/constants';
import {useIVStore, getSetupFormData} from '@chekin/shared';
import {buildSelectOption} from 'utils/common';
import {useAutofilledFieldsStore} from './useAutofilledFields';
import {useIVLockStore, getIVScanForGuest} from './iv/ivLockStore';
import {getLockedFields} from '@chekin/shared/guest-form';

const toFormValues = (stored: FormTypes) =>
  Object.entries(stored).reduce<Record<string, string | Date>>(
    (acc, [fieldName, fieldValue]) => {
      if (fieldValue?.type === FIELD_TYPES.date) {
        acc[fieldName] = dayjs(fieldValue.value).toDate();
      } else {
        acc[fieldName] = fieldValue;
      }
      return acc;
    },
    {},
  );

const omitUndefined = <T extends object>(values: T) =>
  Object.fromEntries(Object.entries(values).filter(([, value]) => value !== undefined));

type UsePreloadGuestDataProps = {
  guestData?: Partial<Guest>;
};
function usePreloadGuestData({guestData}: UsePreloadGuestDataProps = {}) {
  const isDataFromStoragePreloadedRef = React.useRef(false);
  const {reset, getValues} = useFormContext<FormTypes>();
  const {builtPhoneNumberString} = usePreloadPoliceData({
    getValues,
    reset,
  });
  const ivSetupData = useIVStore(getSetupFormData);
  const setAutofilledFields = useAutofilledFieldsStore(state => state.setFields);

  const finishPreloadData = React.useCallback(
    (formData?: {
      [p: string]: string | Date | SelectOption | PhoneDetails | undefined;
    }) => {
      reset({...getValues(), ...formData});
      isDataFromStoragePreloadedRef.current = true;
    },
    [reset, getValues],
  );

  const setIVScan = useIVLockStore(state => state.setScan);

  const buildStorageFormData = React.useCallback(
    (formGuestId?: string) => {
      const storageData = SessionStorage.get<FormTypes>(STORAGE_KEYS.personalFormData);
      const ivScannedData = getIVScanForGuest(formGuestId);
      const scanSource =
        storageData && Object.keys(storageData).length ? storageData : ivScannedData;
      if (!scanSource) return null;

      const storageFormData = {...scanSource} as FormTypes;
      if (ivScannedData) {
        for (const field of getLockedFields(ivScannedData)) {
          storageFormData[field] = ivScannedData[field] as FormTypes[string];
        }
      }

      const formData = toFormValues(storageFormData);

      return {
        ivScannedData,
        values: {
          [GUEST_FORM_NAMES.nationality]: buildSelectOption(ivSetupData.nationality),
          [GUEST_FORM_NAMES.document_type]: buildSelectOption(
            ivSetupData.documentType?.value,
          ),
          ...formData,
          [GUEST_FORM_NAMES.second_phone]: builtPhoneNumberString,
          [EXTRA_FORM_NAMES.secondPhoneDetails]:
            getValues()?.[EXTRA_FORM_NAMES.secondPhoneDetails],
        },
        scannedNames: Object.keys(formData).filter(name => Boolean(formData[name])),
      };
    },
    [ivSetupData, builtPhoneNumberString, getValues],
  );

  const preloadPersistedFormData = React.useCallback(
    (
      guestId: string | undefined,
      mergeWithStorageData: boolean | undefined,
      formGuestId: string | undefined,
    ) => {
      if (isDataFromStoragePreloadedRef.current) return;

      if (guestId && guestData) {
        const formData = buildGuestFormDataFromDB({guestData});
        const ownedScan = getIVScanForGuest(formGuestId);
        if (mergeWithStorageData || ownedScan) {
          const storageData = buildStorageFormData(formGuestId);
          const lockedValues = ownedScan
            ? toFormValues(
                Object.fromEntries(
                  getLockedFields(ownedScan).map(field => [field, ownedScan[field]]),
                ) as FormTypes,
              )
            : {};
          finishPreloadData({
            ...formData,
            ...omitUndefined(storageData?.values ?? {}),
            ...lockedValues,
          });
          setAutofilledFields(storageData?.scannedNames ?? []);
          if (ownedScan) setIVScan(ownedScan);
        } else {
          finishPreloadData(formData);
        }
        SessionStorage.remove(STORAGE_KEYS.personalFormData);
        return;
      }

      const storageData = buildStorageFormData(formGuestId);
      if (!guestId && storageData) {
        finishPreloadData(storageData.values);
        setAutofilledFields(storageData.scannedNames);
        if (storageData.ivScannedData) setIVScan(storageData.ivScannedData);
      }
    },
    [guestData, finishPreloadData, buildStorageFormData, setAutofilledFields, setIVScan],
  );

  const clearPersistedStorageFormData = () => {
    SessionStorage.clear();
  };

  const hasPersistedData = () => {
    const persistedData = SessionStorage.get<FormTypes>(STORAGE_KEYS.personalFormData);
    return Boolean(persistedData);
  };

  return {
    preloadPersistedFormData,
    clearPersistedStorageFormData,
    hasPersistedData,
    isPreloaded: isDataFromStoragePreloadedRef.current,
    // persistFormDataToStorage,
  };
}

export {usePreloadGuestData};
